import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  Page, Hero, StatStrip, ControlRail, Segmented, Sheet, OptionList, Empty,
  useReveal, MoreButton,
  findSkinImage, money, signed, pct, compactMoney,
} from './mobileShared';

// Same header the desktop ticket prints: "Sat, Jan 4, 2026".
const ticketDate = (d) => d.toLocaleDateString('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
});

// Tickets — the sale log. Each sale is one full-width row: what it was on the
// left, the buy → sell route in the middle, and the profit stub pinned right
// so a thumb-scroll down the edge reads as a running P/L tape.

const SORTS = [
  { key: 'date',   label: 'Newest first' },
  { key: 'profit', label: 'Best profit' },
  { key: 'loss',   label: 'Worst profit' },
  { key: 'roi',    label: 'Best ROI' },
];

const OUTCOMES = [
  { key: 'all',  label: 'All' },
  { key: 'win',  label: 'Wins' },
  { key: 'loss', label: 'Losses' },
];

export default function MobileSold({ rows, settings, skinsData = [], onEdit }) {
  const [query, setQuery]     = useState('');
  const [sort, setSort]       = useState('date');
  const [outcome, setOutcome] = useState('all');
  const [filters, setFilters] = useState(false);

  const profC = 'var(--mb-pos)';
  const lossC = 'var(--mb-neg)';

  const sold = useMemo(() => (rows || [])
    .map((row, realIndex) => ({ row, realIndex }))
    .filter(({ row }) => row.skin?.value && parseFloat(row.sellPrice) > 0)
    .map(e => {
      const buy    = parseFloat(e.row.buyPrice) || 0;
      const sell   = parseFloat(e.row.sellPrice) || 0;
      const profit = parseFloat(e.row.profit) || 0;
      return { ...e, buy, sell, profit, roi: buy > 0 ? (profit / buy) * 100 : null, ts: new Date(e.row.timestamp) };
    }), [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = sold.filter(e => {
      if (q && !e.row.skin.value.toLowerCase().includes(q)) return false;
      if (outcome === 'win'  && e.profit < 0) return false;
      if (outcome === 'loss' && e.profit >= 0) return false;
      return true;
    });
    const by = {
      date:   (a, b) => b.ts - a.ts,
      profit: (a, b) => b.profit - a.profit,
      loss:   (a, b) => a.profit - b.profit,
      roi:    (a, b) => (b.roi ?? -Infinity) - (a.roi ?? -Infinity),
    }[sort];
    return [...list].sort(by);
  }, [sold, query, sort, outcome]);

  const totals = useMemo(() => {
    const profit  = sold.reduce((s, e) => s + e.profit, 0);
    const revenue = sold.reduce((s, e) => s + e.sell, 0);
    const cost    = sold.reduce((s, e) => s + e.buy, 0);
    const wins    = sold.filter(e => e.profit >= 0).length;
    return {
      profit, revenue, cost, wins, losses: sold.length - wins,
      winRate: sold.length ? Math.round((wins / sold.length) * 100) : null,
      roi: cost > 0 ? (profit / cost) * 100 : null,
    };
  }, [sold]);

  const shownTotals = useMemo(() => {
    const profit = filtered.reduce((s, e) => s + e.profit, 0);
    const wins   = filtered.filter(e => e.profit >= 0).length;
    return { profit, wins, losses: filtered.length - wins };
  }, [filtered]);

  const [shown, hasMore, showMore] = useReveal(filtered, 14);

  if (!sold.length) {
    return (
      <Page>
        <Empty>No sales recorded yet. Items you mark as sold become tickets here.</Empty>
      </Page>
    );
  }

  return (
    <Page>
      <Hero
        label="Banked profit"
        value={compactMoney(totals.profit)}
        tone={totals.profit >= 0 ? 'up' : 'down'}
        sub={totals.roi != null
          ? `${pct(totals.roi)} on ${money(totals.cost)} spent`
          : 'n/a'}
        foot={`${sold.length} sale${sold.length !== 1 ? 's' : ''} · ${totals.wins}W / ${totals.losses}L`}
      />

      <StatStrip items={[
        { label: 'Revenue', value: money(totals.revenue), sub: `across ${sold.length} sale${sold.length !== 1 ? 's' : ''}` },
        { label: 'Win rate', value: totals.winRate == null ? 'n/a' : `${totals.winRate}%`,
          sub: `${totals.wins} of ${sold.length} made money` },
        { label: 'Cost of goods', value: money(totals.cost), sub: 'paid for what sold' },
        { label: 'Avg per sale', value: sold.length ? signed(totals.profit / sold.length) : 'n/a',
          color: totals.profit >= 0 ? profC : lossC, sub: 'mean profit' },
      ]} />

      <ControlRail
        query={query} onQuery={setQuery}
        placeholder="Search tickets…"
        activeCount={sort !== 'date' ? 1 : 0}
        onOpen={() => setFilters(true)}
      />
      <Segmented options={OUTCOMES} value={outcome} onChange={setOutcome} />

      <div className="mb-resultline">
        {filtered.length} ticket{filtered.length !== 1 ? 's' : ''} · {shownTotals.wins}W/{shownTotals.losses}L ·{' '}
        <b style={{ color: shownTotals.profit >= 0 ? profC : lossC }}>{signed(shownTotals.profit)}</b>
      </div>

      {filtered.length === 0 ? (
        <Empty>No tickets match your filters.</Empty>
      ) : (
        <>
          <div className="mb-list">
            {shown.map((e, i) => {
              const img = findSkinImage(skinsData, e.row.skin.value, e.row.condition?.value);
              const win = e.profit >= 0;
              return (
                <motion.button
                  key={e.realIndex}
                  className={`mb-ticket ${win ? 'win' : 'loss'}`}
                  // Zone paints the whole stub; Classic leaves it dark and
                  // lets the stamp and figure carry the outcome.
                  style={{ '--mb-stub': win ? '#c9f65a' : '#ff9bd2' }}
                  onClick={() => onEdit && onEdit(e.row, e.realIndex)}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 10) * 0.025 }}
                >
                  <div className="mb-ticket-main">
                    <div className="mb-ticket-date">
                      {ticketDate(e.ts)}
                      {e.row.siteFee?.label ? ` · ${e.row.siteFee.label.split(' ')[0]}` : ''}
                    </div>

                    <div className="mb-ticket-body">
                      {img ? <img src={img} alt="" loading="lazy" /> : <span className="mb-ticket-blank" />}
                      <div style={{ minWidth: 0 }}>
                        <div className="mb-ticket-name">{e.row.skin.value}</div>
                        <div className="mb-ticket-sub">
                          {e.row.condition?.value || 'n/a'}
                          {e.row.receivedFromTradeId ? ' · from trade' : ''}
                        </div>
                      </div>
                    </div>

                    <div className="mb-ticket-route">
                      <b>{money(e.buy)}</b>
                      <span className="track" />
                      <b>{money(e.sell)}</b>
                    </div>
                  </div>

                  <div className="mb-ticket-perf" aria-hidden="true" />

                  <div className="mb-ticket-stub">
                    <span className={`mb-ticket-stamp ${win ? 'win' : 'loss'}`}>{win ? 'WIN' : 'LOSS'}</span>
                    <span className="mb-ticket-profit" style={{ color: win ? profC : lossC }}>
                      {signed(e.profit)}
                    </span>
                    {e.roi != null && (
                      <span className="mb-ticket-roi" style={{ color: win ? profC : lossC }}>{pct(e.roi)}</span>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
          {hasMore && <MoreButton onClick={showMore} remaining={filtered.length - shown.length} />}
        </>
      )}

      <Sheet open={filters} onClose={() => setFilters(false)} title="Sort tickets">
        <OptionList label="Sort by" value={sort} onChange={v => { setSort(v); setFilters(false); }} options={SORTS} />
      </Sheet>
    </Page>
  );
}
